import { common as c, operators as o } from '@pendo/aggregations';
import { DxEvent, DxEventType, DxFilter, FilterOperator, FilterType } from '@middleware/types';
import { getFilterIdentifier } from '@middleware/common/utils';
import isArray from 'lodash/isArray';

export function createFilterString (
    filter: DxFilter,
    getIdentifierFn: (filter: DxFilter) => string | undefined = getFilterIdentifier,
): string {
    const identifier = getIdentifierFn(filter) || '';
    // Note that aggs don't support Javascript things like Infinity and NaN.
    // No errors will occur, but comparisons will always return false.
    const firstValue = +filter.comparisonValues[0];
    const comparisonValues = isArray(filter.comparisonValues) ? filter.comparisonValues : [filter.comparisonValues];
    let commaSeparatedValues;
    let formattedValues: string[];
    if (filter.filterType === FilterType.Location && filter.field === 'country') {
        formattedValues = comparisonValues
            .map((val) => {
                const code = val;

                return JSON.stringify(code);
            })
            .filter(Boolean);
        commaSeparatedValues = formattedValues.join(',');
    } else if (filter.filterType === FilterType.Location && filter.field === 'region') {
        formattedValues = comparisonValues
            .map((val) => {
                const parts = val.split('-');
                if (parts.length === 2) {
                    const countryCode = parts[0];
                    const regionCode = parts[1];
                    const code = `${countryCode.toUpperCase()}-${regionCode.toLowerCase()}`;

                    return JSON.stringify(code);
                }

                return undefined;
            })
            .filter(Boolean) as string[];
        commaSeparatedValues = formattedValues.join(',');
    } else {
        formattedValues = filter.comparisonValues.map((val) => JSON.stringify(val));
        commaSeparatedValues = formattedValues.join(',');
    }

    switch (filter.operator) {
        case FilterOperator.True:
            return `${identifier} == true`;
        case FilterOperator.False:
            return `${identifier} == false`;
        case FilterOperator.Equal:
            // Equals one of the given values
            return `contains([${commaSeparatedValues}], ${identifier})`;
        case FilterOperator.NotEqual:
            // Is not equal to ANY of the given values
            return `!contains([${commaSeparatedValues}], ${identifier})`;
        case FilterOperator.LessOrEqual:
            return `${identifier} <= ${firstValue}`;
        case FilterOperator.GreaterOrEqual:
            return `${identifier} >= ${firstValue}`;
        case FilterOperator.Contains:
            // Contains one of the given values
            return formattedValues.map((val) => `contains(${identifier}, ${val})`).join('||');
        case FilterOperator.NotContains:
            // Does not contain ANY of the given values (De Morgan's Law)
            return formattedValues.map((val) => `!contains(${identifier}, ${val})`).join('&&');
        case FilterOperator.Empty:
            return `isEmpty(${identifier})`;
        case FilterOperator.NotEmpty:
            return `!isEmpty(${identifier})`;
    }
}

/**
 * Create a filter pipeline based on the given targets and query filters.
 *
 * For single-target queries, this will just be each query filter, joined by &&.
 *
 * For multi-target queries, the filter will be clauses of this form, joined by &&:
 * ((targetId == "the-id" && (filters for target)) || targetId != "the-id")
 *
 * If there are no filters provided, then an empty pipeline is returned (no
 * filter operator).
 */
export default function generateAggregationFilter ({
    targets,
    targetFilters,
    reportFilters,
    type,
}: {
    targets: DxEvent[];
    targetFilters: Record<string, DxFilter[]>;
    reportFilters: DxFilter[];
    type: DxEventType;
}): c.PipelineData[] {
    const targetFilterStrings = targets.reduce((arr, { id }) => {
        const filters = targetFilters[id];

        if (filters?.length) {
            const filterStrings = filters.map((filter) => createFilterString(filter));
            const filterString = filterStrings.map((str) => `(${str})`).join('&&');
            arr.push([JSON.stringify(id), filterString]);
        }

        return arr;
    }, [] as [string, string][]);

    let combinedTargetFilterString;
    if (targetFilterStrings.length) {
        const { sourceId } = type;
        combinedTargetFilterString =
            targets.length === 1
                ? targetFilterStrings[0][1]
                : targetFilterStrings
                    .map(([id, filter]) => `((${sourceId} == ${id} && (${filter})) || ${sourceId} != ${id})`)
                    .join('&&');
    }

    const reportFilterStrings = reportFilters.map((filter) => createFilterString(filter));
    const reportFilterString = reportFilterStrings.map((str) => `(${str})`).join('&&');

    const combinedFilterString = [combinedTargetFilterString, reportFilterString].filter(Boolean).join('&&');

    if (!combinedFilterString) return [];

    return [o.filter(combinedFilterString)];
}
